/**
 * 키오스크 활동 이벤트 전송 (BOOTH-87) — 큐에 모아 `POST /api/booth/:slug/activity` 로 배치 전송한다.
 *
 * - 라우트 전환은 `trackRoute`(router store 의 `go` 가 호출) 하나로, 화면 안 행동은 `track(type, fields)`.
 * - 5초마다 또는 10건이 쌓이면 flush. 페이지가 숨겨지거나 닫히면 `sendBeacon` 으로 남은 것을 보낸다.
 * - **실패는 조용히 버린다** — 통계가 손님 플로우를 방해하면 안 된다. 재시도 없음(큐 상한 200).
 * - 이 모듈은 router store 를 import 하지 않는다(순환 방지). 현재 상태는 store 가 `bindActivitySnapshot` 으로 넘긴 함수로 읽고,
 *   엔드포인트의 부스 slug 는 App 이 `setActivityBoothSlug` 로 넘긴다.
 */
import type { ClientErrorCode, KioskActivityInput, KioskActivityType, LocaleCode, VisitEndReason } from "@fiket/booth-schema";
import type { Route } from "../store/router";
import { FLUSH_AFTER_MS, FLUSH_AT, capQueue, shouldFlush, takeBatch, transitionEvents } from "./activityQueue";
import { buildErrorDetail, resolveErrorMessage, shouldDedupe, type TrackErrorFields } from "./errorMessage";

export interface ActivitySnapshot {
  route: Route;
  visitId: string | null;
  themeId: string | null;
  styleId: string | null;
  language: LocaleCode;
}

let snapshot: (() => ActivitySnapshot) | null = null;
let boothSlug: string | null = null;
const queue: KioskActivityInput[] = [];
let timer: number | null = null;
let routeEnteredAt = Date.now();
/** visit_end 를 보낸 방문 — 같은 방문에 두 번 보내지 않는다 (thanks 뒤 리셋 등) */
let endedVisitId: string | null = null;
let listenersBound = false;

export function bindActivitySnapshot(fn: () => ActivitySnapshot): void {
  snapshot = fn;
}

export function setActivityBoothSlug(slug: string): void {
  boothSlug = slug;
  bindPageListeners();
  if (queue.length > 0) scheduleFlush();
}

function endpoint(): string | null {
  return boothSlug ? `/api/booth/${encodeURIComponent(boothSlug)}/activity` : null;
}

function enqueue(events: readonly KioskActivityInput[]): void {
  if (events.length === 0) return;
  queue.push(...events);
  capQueue(queue);
  if (queue.length >= FLUSH_AT) void flushActivity();
  else scheduleFlush();
}

function scheduleFlush(): void {
  if (timer !== null || typeof window === "undefined") return;
  timer = window.setTimeout(() => {
    timer = null;
    void flushActivity();
  }, FLUSH_AFTER_MS);
}

/**
 * 큐를 비워 보낸다. 엔드포인트가 아직 없으면 보류(slug 가 정해지면 다시 예약).
 * 오프라인이면 보내지 않고 타이머만 다시 걸어 둔다 — 복귀하면 `installConnectivityListeners` 가 바로 부른다 (BOOTH-97).
 */
export async function flushActivity(): Promise<void> {
  const url = endpoint();
  if (!url || queue.length === 0) return;
  const onLine = typeof navigator === "undefined" ? true : navigator.onLine;
  if (!shouldFlush(onLine, queue.length)) {
    scheduleFlush();
    return;
  }
  while (queue.length > 0) {
    const batch = takeBatch(queue);
    try {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
    } catch {
      // 네트워크 실패 — 버린다. 통계는 best-effort.
    }
  }
}

/** 페이지 이탈·숨김 시 남은 큐를 beacon 으로 — fetch 는 언로드 중 끊길 수 있다 */
function flushWithBeacon(): void {
  const url = endpoint();
  if (!url || queue.length === 0) return;
  while (queue.length > 0) {
    const batch = takeBatch(queue);
    const body = new Blob([JSON.stringify({ events: batch })], { type: "application/json" });
    if (typeof navigator === "undefined" || !navigator.sendBeacon || !navigator.sendBeacon(url, body)) {
      void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  }
}

function bindPageListeners(): void {
  if (listenersBound || typeof document === "undefined") return;
  listenersBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushWithBeacon();
  });
  window.addEventListener("pagehide", flushWithBeacon);
}

function withSnapshot(input: Omit<KioskActivityInput, "clientAt" | "visitId" | "route" | "language">): KioskActivityInput {
  const s = snapshot?.();
  return {
    ...input,
    clientAt: new Date().toISOString(),
    ...(s?.visitId ? { visitId: s.visitId } : {}),
    ...(s ? { route: s.route, language: s.language } : {}),
  };
}

/** `track` 으로 실어 보낼 수 있는 필드 — 오류·상태 신호용 필드 포함 (BOOTH-97) */
export type TrackFields = Partial<
  Pick<
    KioskActivityInput,
    "themeId" | "styleId" | "durationMs" | "language" | "code" | "message" | "appVersion" | "userAgent" | "viewport"
  >
>;

/** 화면 안 행동 — 카운트다운·촬영·다시 찍기·언어 변경·오류·상태 신호. 현재 화면·방문·언어는 스냅샷에서 채운다. */
export function track(
  type: Exclude<KioskActivityType, "screen" | "visit_start" | "visit_end">,
  fields: TrackFields = {}
): void {
  enqueue([withSnapshot({ type, ...fields })]);
}

/** 마지막으로 보낸 오류 — 같은 오류가 10초 안에 반복되면 억제한다 (루프가 큐를 채우는 것 방지) */
let lastError: { key: string; at: number } | null = null;

/**
 * 클라이언트 오류 한 건 (BOOTH-97). `err` 를 주면 정제해 담고, 호출자가 조립한 문구는 `fields.message` 로 넘긴다.
 * 두 경로 모두 `resolveErrorMessage` 가 정제·절단한다. 같은 `code|message` 는 10초에 한 번만.
 *
 * BOOTH-109: 행 `message` 와 별개로 `detail`(우리 API 의 `code`/`httpStatus` 를 덕타이핑으로 풀고, `err.stack` 또는
 * `fields.stackText`, `fields.url`) 을 같이 보낸다 — 서버가 activity-errors 테이블에 상세로 저장하고 콘솔 시트가 보여준다.
 */
export function trackError(code: ClientErrorCode, err?: unknown, fields: TrackErrorFields = {}): void {
  const message = resolveErrorMessage(err, fields.message);
  const now = Date.now();
  const key = `${code}|${message ?? ""}`;
  if (shouldDedupe(lastError, key, now)) return;
  lastError = { key, at: now };
  const detail = buildErrorDetail(err, fields);
  enqueue([
    withSnapshot({
      type: "client_error",
      code,
      ...(message === undefined ? {} : { message }),
      ...(detail ? { detail } : {}),
    }),
  ]);
}

/**
 * 라우트 전환 — store `go()` 가 전환 **뒤** 상태로 호출한다(`visitId` 는 시작이면 새 값). 같은 방문의 visit_end 는 한 번만.
 */
export function trackRoute(prev: Route, next: Route, after: Omit<ActivitySnapshot, "route">): void {
  const now = Date.now();
  const events = transitionEvents(prev, next, { ...after, prevEnteredAt: routeEnteredAt, now });
  routeEnteredAt = now;
  const filtered = events.filter((e) => {
    if (e.type !== "visit_end") return true;
    if (!after.visitId || endedVisitId === after.visitId) return false;
    endedVisitId = after.visitId;
    return true;
  });
  enqueue(filtered);
}

/** 방문 종료 — 무동작 리셋(idle)·"처음으로"(home)·그 외 리셋(reset). `go("thanks")` 는 trackRoute 가 thanks 로 기록한다. */
export function trackVisitEnd(reason: VisitEndReason): void {
  const s = snapshot?.();
  if (!s?.visitId || endedVisitId === s.visitId) return;
  endedVisitId = s.visitId;
  enqueue([withSnapshot({ type: "visit_end", reason })]);
}
